import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider';
import Practice from 'logic/Practice';
import Nurse from 'logic/Nurse';
import { useAPI } from 'contexts/APIProvider';
import React from 'react'
import { MatchedJobForm } from 'types/interfaces';
import update from 'immutability-helper';
import MatchedJobFormError from 'errors/MatchedJobFormError';
import { v4 as uuidv4 } from 'uuid';
import { Button, MenuItem } from '@mui/material';
import Header from 'components/Header';
import { Box } from '@mui/material';
import { get, isEmpty, partial } from 'lodash';
import PrematchedJobForm from './PrematchedJobForm';
import AutoCompleteSelectField from 'components/AutoCompleteSelectField';
import { DateTime } from 'luxon';
import CheckboxSelectField from 'components/CheckboxSelectField';
import { Parking, Role } from 'types/types';
import FormField from 'components/FormField';
import { plainToClass } from 'class-transformer';

export type PostPrematchedJobStatus = 'success' | 'error' | 'pending'

const AdminPostPrematchedJobs: React.FC = () => {
    const { api } = useAPI()
    const { showAlert } = useSnackBarAlert()
    const [practices, setPractices] = React.useState<Practice[]>([])
    const [nurses, setNurses] = React.useState<Nurse[]>([])
    const [selectedPracticeId, setSelectedPracticeId] = React.useState<number>()
    const [selectedNurseId, setSelectedNurseId] = React.useState<number>()
    const [role, setRole] = React.useState<Role>('dental nurse')
    const [parking, setParking] = React.useState<Parking>('not specified')
    const [forms, setForms] = React.useState<Record<string, MatchedJobForm>>({})
    const [errors, setErrors] = React.useState<Record<string, MatchedJobFormError>>({})
    const [statuses, setStatuses] = React.useState<Record<string, PostPrematchedJobStatus>>({})
    const [saving, setSaving] = React.useState(false)

    const defaultForm = React.useMemo(() => ({
        date: DateTime.now().toFormat('yyyy-MM-dd'),
        start_time: '09:00',
        end_time: '17:00',
        lunch_break: 30,
    }), [])

    const updateForm = React.useCallback((formKey: string, name: string, value: any) => {
        setForms(update(forms, { [formKey]: { [name]: { $set: value } } }))
    }, [forms])

    const setForm = React.useCallback((formKey: string, form: MatchedJobForm) => {
        setForms(update(forms, { [formKey]: { $set: form } }))
    }, [forms])

    const addForm = React.useCallback((form?: MatchedJobForm) => {
        const newKey = uuidv4()
        setForms(update(forms, { $merge: { [newKey]: {
            ...defaultForm,
            ...(form ?? {}),
        } } }))
        setStatuses(update(statuses, { $merge: { [newKey]: 'pending' } }))
    }, [defaultForm, forms, statuses])

    const removeForm = React.useCallback((formKey: string) => {
        setForms(update(forms, { $unset: [formKey] }))
        setErrors(update(errors, { $unset: [formKey] }))
        setStatuses(update(statuses, { $unset: [formKey] }))
    }, [errors, forms, statuses])

    const fetchPractices = React.useCallback(async (searchTerm: string) => {
        if (!searchTerm) return
        setPractices(await api.listPractices({ name: searchTerm }))
    }, [api])

    const fetchNurses = React.useCallback(async (searchTerm: string) => {
        if (!searchTerm) return
        setNurses(await api.listNurses({ name: searchTerm }))
    }, [api])

    const practiceSelectOptions = React.useMemo(() => {
        return practices.map((practice) => ({
            value: practice.practice_id,
            label: practice.enabled ? practice.practice_name : `${practice.practice_name} (disabled)`,
        }))
    }, [practices])

    const nurseSelectOptions = React.useMemo(() => {
        return nurses.map((nurse) => ({
            value: nurse.nurse_id,
            label: nurse.full_name,
        }))
    }, [nurses])

    const save = React.useCallback(async () => {
        try {
            setSaving(true)
            const newStatuses: Record<string, PostPrematchedJobStatus> = {}
            const newErrors: Record<string, MatchedJobFormError> = {}
            const unsavedForms = Object.entries(forms).filter(([formKey, form]) => statuses[formKey] !== 'success')
            for (const [formKey, form] of unsavedForms) {
                try {
                    await api.createMatchedJob({
                        practice_id: selectedPracticeId,
                        nurse_id: selectedNurseId,
                        hygienist_required: role === 'hygienist',
                        parking,
                        ...form,
                    })
                    newStatuses[formKey] = 'success'
                } catch (error) {
                    if (error instanceof MatchedJobFormError) {
                        newErrors[formKey] = error
                    } else if (Array.isArray(error)) {
                        newErrors[formKey] = plainToClass(MatchedJobFormError, { _schema: error })
                    } else {
                        showAlert('error', String(error))
                    }
                    newStatuses[formKey] = 'error'
                }
            }
            setStatuses(update(statuses, { $merge: newStatuses }))
            setErrors(newErrors)
        } finally {
            setSaving(false)
        }
    }, [api, forms, selectedPracticeId, selectedNurseId, role, parking, showAlert, statuses, setStatuses, setErrors])

    const canSave = React.useMemo(() => {
        return (
            !isEmpty(forms) 
            && selectedPracticeId 
            && selectedNurseId 
            && parking !== 'not specified'
        )
    }, [forms, selectedPracticeId, selectedNurseId, parking])

    React.useEffect(() => {
        addForm()  // start with one form on page load
    }, [])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
            <Header text='Post Prematched Jobs'>
                <Button variant='contained' color='primary' onClick={save} disabled={!canSave || saving}>
                    Post All Jobs
                </Button>
            </Header>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <AutoCompleteSelectField
                    label='Select a practice'
                    sx={{ width: '200px' }}
                    options={practiceSelectOptions}
                    fetchOptions={fetchPractices}
                    value={selectedPracticeId ?? ''}
                    onChange={(value) => setSelectedPracticeId(Number(value))}
                    errors={[]}
                />
                <AutoCompleteSelectField
                    label='Select a nurse'
                    sx={{ width: '200px' }}
                    options={nurseSelectOptions}
                    fetchOptions={fetchNurses}
                    value={selectedNurseId ?? ''}
                    onChange={(value) => setSelectedNurseId(Number(value))}
                    errors={[]}
                />
                <CheckboxSelectField 
                    onChange={(value) => setRole(value as Role)}
                    value={role}
                    sx={{ flexDirection: 'row' }}
                    options={[{
                        value: 'dental nurse',
                        label: 'Dental Nurse',
                        }, {
                        value: 'hygienist',
                        label: 'Hygienist',
                        // disabled: true,
                    }]} 
                    errors={[]}
                />
                <FormField
                    select
                    name='parking'
                    label='Parking'
                    onChange={(e) => setParking(e.target.value as Parking)}
                    value={parking}
                    errors={[]}
                >
                    <MenuItem value='not specified'>Not Specified</MenuItem>
                    <MenuItem value='free parking available'>Free Parking Available</MenuItem>
                    <MenuItem value='paid parking available'>Paid Parking Available</MenuItem>
                    <MenuItem value='no parking available'>No Parking Available</MenuItem>
                </FormField>
            </Box>
            {Object.entries(forms).map(([formKey, form]) => (
                <PrematchedJobForm 
                    key={formKey} 
                    role={role}
                    form={form} 
                    error={get(errors, formKey)} 
                    status={statuses[formKey]}
                    updateForm={partial(updateForm, formKey)} 
                    setForm={partial(setForm, formKey)}
                    addForm={addForm} 
                    removeForm={partial(removeForm, formKey)} 
                    saving={saving}
                />
            ))}
        </Box>
    )
}

export default AdminPostPrematchedJobs